import { useLayoutEffect, useMemo, useRef, useState, type MouseEvent } from 'react';

import type { TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import {
  chapterListWidth,
  chapterMenuWidth,
  CHAPTER_MENU_DESKTOP_QUERY,
  CHAPTER_MENU_HALF,
} from '@/features/reader/utils/chapter-menu-width.util';

type UseChapterMenuWidthParams = {
  chapters: TChapterWithoutContent[];
  selectedChapterId: string;
  setSelectedChapterId: (chapterId: string) => void;
  onOpenChange?: (open: boolean) => void;
};

export function useChapterMenuWidth({
  chapters,
  selectedChapterId,
  setSelectedChapterId,
  onOpenChange,
}: UseChapterMenuWidthParams) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const probeRef = useRef<HTMLDivElement>(null);
  const [menuWidth, setMenuWidth] = useState<number | null>(null);

  const chapter = useMemo(() => {
    return chapters.find((item) => item.id === selectedChapterId);
  }, [chapters, selectedChapterId]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const onSelectChapter = (event: MouseEvent<HTMLAnchorElement>, chapterId: string) => {
    event.preventDefault();
    setSelectedChapterId(chapterId);
    handleOpenChange(false);
  };

  useLayoutEffect(() => {
    const trigger = triggerRef.current;
    const probe = probeRef.current;
    if (!trigger || !probe) return;

    const panel = trigger.closest('[data-reader-toolbar]');
    if (!(panel instanceof HTMLElement)) return;

    const apply = () => {
      const desktop = window.matchMedia(CHAPTER_MENU_DESKTOP_QUERY).matches;
      if (!desktop) {
        trigger.style.flex = '';
        trigger.style.width = '';
        trigger.style.maxWidth = '';
        delete trigger.dataset.chapterWide;
        setMenuWidth(null);
        return;
      }

      const panelWidth = panel.getBoundingClientRect().width;
      const half = panelWidth * CHAPTER_MENU_HALF;
      const titleWrap = trigger.firstElementChild;
      trigger.style.flex = '0 0 auto';
      trigger.style.width = 'max-content';
      trigger.style.maxWidth = 'none';
      if (titleWrap instanceof HTMLElement) {
        titleWrap.style.flex = 'none';
        titleWrap.style.width = 'max-content';
      }
      const selectedWidth = trigger.offsetWidth;
      if (titleWrap instanceof HTMLElement) {
        titleWrap.style.flex = '';
        titleWrap.style.width = '';
      }

      const longestItem = Array.from(probe.children).reduce((max, child) => {
        if (!(child instanceof HTMLElement)) return max;
        return Math.max(max, child.offsetWidth);
      }, 0);
      const actions = panel.querySelector(':scope > .ml-auto');
      const gap = Number.parseFloat(getComputedStyle(panel).columnGap) || 0;
      const actionsWidth = actions instanceof HTMLElement ? actions.offsetWidth : 0;
      const available = Math.max(half, panelWidth - actionsWidth - gap);

      const triggerWidth = chapterMenuWidth(half, selectedWidth, available);
      trigger.style.width = `${triggerWidth}px`;
      trigger.style.maxWidth = `${triggerWidth}px`;
      if (selectedWidth <= half) delete trigger.dataset.chapterWide;
      else trigger.dataset.chapterWide = 'true';

      const nextMenuWidth = chapterMenuWidth(half, chapterListWidth(longestItem), panelWidth);
      setMenuWidth((current) => (current === nextMenuWidth ? current : nextMenuWidth));
    };

    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [chapters, selectedChapterId]);

  return {
    chapter,
    isOpen,
    handleOpenChange,
    onSelectChapter,
    triggerRef,
    probeRef,
    menuWidth,
  };
}
